import React from "react";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { ReactComponent as UnsortIcon } from "../../../Assets/rank-tracking-assets/unshort.svg";
import Tooltip from "../../components/tooltip";
import { Checkbox } from "@mui/material";
import BacklinkGapTooltip from "../../components/ToolTip/BacklinkGapTooltip";
import VirtualizeTable from "../DynamicTable/VirtualizeTable";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { rangeCircleStyle } from "../../../../helperJs/helper";
import FallbackLoader from "../../components/FallbackLoader";

import Ellipsis from "@semcore/ellipsis";
import Chip from "@mui/material/Chip";
import FreeTrialDialog from "../../components/dialog/FreeTrialDialog";

const columnArr = [
  {
    label: "Source page Title & URL",
    dataKey: "target_page",
    width: "300px",
    sort: false,
    align: "left",
    tooltip:
      "SEO Title / URL - The title that shows up in a Google search for any given URL, followed by the page URL itself.",
  },
  {
    label: "DR",
    dataKey: "domain_authority",
    sort: true,
    align: "center",
    width: "100px",
    tooltip:
      "Domain Rank: A metric that denotes the domain rank, referring to the target page or domain, calculated by summing each page’s rank value, narrowed down to a range on the scale. 0 range means new domain with limited backlinks, 5-50 range means older domains with higher quality backlinks, 50+ scale means huge domains with significant authority backlinks.",
  },
  {
    label: "PR",
    dataKey: "page_authority",
    sort: true,
    align: "center",
    width: "100px",
    tooltip:
      "Page Rank: Rank of a page referring to the specified domain or target page. It is calculated based on number of inbound linking pages, their PageRank, number of outbound links from these pages, and damping factor – determined by user bahavior navigating the web clicking on these links.",
  },
  {
    label: "SS",
    dataKey: "backlink_spam_score",
    width: "100px",
    sort: true,
    align: "center",
    tooltip:
      "Spam Score: Metric that designates the spam level of a page, pages or an entire domain, with corresponding percentage score from 0% to 100% (higher percentage means higher spam probability). It is impacted by 18 signals (in positive and negative way), including domain name length, site safety protocol, contact info on page, internal and external links, and many more.",
  },
  {
    label: "Anchor text  | Target page",
    dataKey: "anchor",
    width: "200px",
    sort: false,
    align: "center",
    tooltip:
      "Anchor Text - The Anchor text is the visible, clickable text in a hyperlink.",
  },
  {
    label: (
      <>
        First seen
        <br />
        Last seen
      </>
    ),
    dataKey: "first_seen",
    width: "160px",
    sort: true,
    align: "center",
    tooltip: (
      <>
        First Seen - The first time when our robots were able to identify this
        backlink.
        <br />
        Last Seen - The last time our robots were able to confirm the existence
        of this backlink.
      </>
    ),
  },
];

const ranges = [
  { min: -Infinity, max: 30, backgroundColor: "rgb(108, 170, 106)" },
  { min: 30, max: 60, backgroundColor: "rgb(255, 142, 59)" },
  { min: 60, max: Infinity, backgroundColor: "rgb(233, 86, 85)" },
  { default: true, backgroundColor: "rgb(233, 86, 85)" }, // Default color
];

function isURL(str) {
  var urlPattern = /(\.com|https|www|http)/gi;
  return urlPattern.test(str);
}

const extractURL = (text) => {
  const match = text.match(/https?:\/\/[^\s)"]+/);
  return match ? match[0] : null; // Return the URL or null if not found
};

function Row(props) {
  const { row, header, isCheckboxChecked, handleTableSelectedData } = props;
  const [open, setOpen] = React.useState(false);

  const rowData = React.useMemo(() => {
    if (row?.data?.length === 0) return [];
    return row?.data?.map((item, index) => {
      return {
        id: index,
        domain_authority: (
          <>
            {Math.round(item?.domain_authority / 10) < 5
              ? 0
              : Math.round(item?.domain_authority / 10) || 0}
          </>
        ),
        target_page: (
          <>
            <div
              className="d-flex flex-column align-items-start"
              style={{ maxWidth: "300px" }}
            >
              {item?.page_from_title && (
                <span className="backlink-table-head mt-0">
                  <a
                    href={item?.url_from}
                    className="w-100"
                    target="_blank"
                    style={{
                      color: "#000000E5",
                      paddingLeft: "0px",
                    }}
                    rel="noopener noreferrer"
                  >
                    {item?.page_from_title}
                  </a>
                </span>
              )}
              {item?.url_from && (
                <span className="d-flex align-items-center w-100">
                  <a
                    href={item?.url_from}
                    target="_blank"
                    className="d-flex align-items-center"
                    rel="noopener noreferrer"
                    style={{ wordBreak: "break-all" }}
                  >
                    {item?.url_from}
                  </a>
                </span>
              )}
            </div>
          </>
        ),
        page_authority: (
          <>
            {Math.round(item?.page_authority / 10) < 5
              ? 0
              : Math.round(item?.page_authority / 10) || 0}
          </>
        ),
        backlink_spam_score: (
          <>
            <span
              className="spamscores-table me-1"
              style={rangeCircleStyle(item?.backlink_spam_score, ranges)}
            ></span>
            <span>{`${item?.backlink_spam_score}%`}</span>
          </>
        ),
        anchor: (
          <>
            <div className="d-flex flex-column align-items-start text-start">
              {item?.anchor && (
                <>
                  {isURL(item?.anchor) ? (
                    <span className="d-flex align-items-center w-100">
                      <a
                        href={extractURL(item?.anchor)}
                        target="_blank"
                        className="d-flex align-items-center w-100"
                        rel="noopener noreferrer"
                      >
                        <Ellipsis wMax={180} trim="end">
                          {extractURL(item?.anchor)}
                        </Ellipsis>
                        <OpenInNewIcon
                          sx={{
                            fontSize: "13px",
                            marginLeft: "1px",
                          }}
                        />
                      </a>
                    </span>
                  ) : (
                    <Ellipsis wMax={200} trim="end">
                      {item.anchor}
                    </Ellipsis>
                  )}
                </>
              )}
              <Chip
                label={item.dofollow ? "follow" : "unfollow"}
                size="small"
                sx={{
                  fontSize: "0.9em",
                  color: "#fff",
                  backgroundColor: item.dofollow ? "#7BBA75" : "#F98887",
                }}
              />

              {item?.url_to && (
                <a
                  href={item?.url_to}
                  target="_blank"
                  className="d-flex align-items-center mt-1"
                  rel="noopener noreferrer"
                  style={{ wordBreak: "break-all" }}
                >
                  {item?.url_to}
                </a>
              )}
            </div>
          </>
        ),
        first_seen: (
          <>
            {item?.first_seen.split(" ", 1)}
            <br />
            {item?.last_seen.split(" ", 1)}
          </>
        ),
      };
    });
  }, [row]);

  function fixedHeaderContent() {
    return (
      <TableRow>
        {columnArr.map((column) => (
          <TableCell
            key={column.dataKey}
            variant="head"
            align={column.align}
            className="font-medium"
            sx={{
              backgroundColor: "#F4F5F9",
              fontSize: "0.9em",
              color: "#191B23",
              padding: "10px",
              width: column?.width,
            }}
          >
            <span className={"point"}>
              {column.label}
              {column?.tooltip && <Tooltip title={column.tooltip} />}
            </span>
          </TableCell>
        ))}
      </TableRow>
    );
  }

  function rowContent(_index, row) {
    return (
      <React.Fragment>
        {columnArr.map((column) => (
          <TableCell
            key={column.dataKey}
            align={column.align}
            className="font-medium"
            sx={{
              fontSize: "1em",
              color: "#191B23",
              padding: "10px",
            }}
          >
            {row[column.dataKey]}
          </TableCell>
        ))}
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell
          padding="checkbox"
          key={"checkbox" + row.id}
          sx={{
            fontSize: "1em",
            color: "#191B23",
            padding: "5px 8px",
          }}
          onClick={() => handleTableSelectedData(row.domain)}
        >
          <Checkbox
            className="p-1"
            sx={{
              color: "#C4C7CF",
              "&.Mui-checked": {
                color: "#365486",
              },
            }}
            checked={isCheckboxChecked(row.domain)}
          />
        </TableCell>
        {header?.map((column) => {
          return (
            <TableCell
              key={column.dataKey}
              align={column.align}
              sx={{
                fontSize: "1em",
                color: "#191B23",
                padding: "5px",
              }}
            >
              {row[column.dataKey]}
            </TableCell>
          );
        })}
        <TableCell
          sx={{
            fontSize: "1em",
            color: "#191B23",
            padding: "5px 8px",
          }}
        >
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {" "}
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell className="p-0" colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <VirtualizeTable
              {...{
                data: rowData,
                columns: header,
                columnLength: header?.length,
                rowContent,
                fixedHeaderContent,
              }}
            />
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

export default function CollapsibleTable({
  header,
  rows,
  requestSort,
  handleTableSelectAll,
  isSelectAll,
  isCheckboxChecked,
  tableLoader,
  isTableBlur,
  handleTableSelectedData,
}) {
  sessionStorage.setItem("FREE_TRIAL_TABLE", "gapTable");
  return (
    <TableContainer component={Paper} sx={{ maxHeight: 700 }}>
      <Table stickyHeader aria-label="collapsible table">
        <TableHead sx={{ position: "relative", zIndex: "3" }}>
          <TableRow>
            <TableCell
              padding="checkbox"
              key={"checkbox"}
              variant="head"
              align="left"
              className="font-medium"
              sx={{
                backgroundColor: "#F4F5F9",
                fontSize: "0.9em",
                color: "#191B23",
                padding: "5px 8px",
              }}
              onClick={handleTableSelectAll}
            >
              <Checkbox
                className="p-1"
                sx={{
                  color: "#C4C7CF",
                  "&.Mui-checked": {
                    color: "#365486",
                  },
                }}
                checked={isSelectAll}
                readOnly
              />
            </TableCell>
            {header?.map((column) => {
              return (
                <TableCell
                  key={column.dataKey}
                  variant="head"
                  align={column.align}
                  className="font-medium"
                  sx={{
                    fontSize: "0.9em",
                    color: "#191B23",
                    padding: "5px",
                    backgroundColor: "#F4F5F9",
                  }}
                >
                  <span
                    className="point d-flex align-items-center"
                    onClick={() => {
                      column?.sort && requestSort(column.dataKey);
                    }}
                  >
                    {column?.svg && column.svg}
                    {column.label}
                    {column?.sort && (
                      <span className="ms-2">
                        <UnsortIcon />
                      </span>
                    )}
                    {column?.tooltip && <Tooltip title={column.tooltip} />}
                    {column?.customToolTip && (
                      <BacklinkGapTooltip
                        {...{
                          ref_domain: column.customToolTip.ref_domain,
                          backlinks: column.customToolTip.backlinks,
                        }}
                      />
                    )}
                  </span>
                </TableCell>
              );
            })}
            <TableCell
              variant="head"
              align="center"
              sx={{
                padding: "5px",
                backgroundColor: "#F4F5F9",
              }}
            />
          </TableRow>
        </TableHead>
        <TableBody>
          {tableLoader ? (
            <TableCell colSpan={header?.length + 2}>
              <FallbackLoader />
            </TableCell>
          ) : isTableBlur ? (
            <TableCell
              colSpan={header?.length + 2}
              sx={{ padding: "0", position: "relative" }}
            >
              <FreeTrialDialog />
            </TableCell>
          ) : (
            rows.map((row) => (
              <Row
                key={row.domain}
                row={row}
                {...{
                  row,
                  header,
                  isCheckboxChecked,
                  handleTableSelectedData,
                }}
              />
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
